/**
 * List-page chrome for the Display, Performance Max and Shopping workspaces —
 * the same look as the YouTube and Search workspaces: toolbar · stat tiles ·
 * filter bar · campaign cards that expand into their children · list / grid.
 * Each objective supplies its own children and grid cards.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ArrowUpDown, BarChart3, ChevronDown, ChevronRight, Globe, LayoutGrid, List, Plus, RefreshCw, Search, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import { AttachCustomerDialog } from '@/components/google-ads/AttachCustomerDialog';
import '@/components/google-ads/google-ads.css';
import { googleAdsService, GoogleAdsCampaignSummary } from '@/services/google-ads.service';
import {
  alreadyToasted, CampaignDates, errMsg, Pill, Spinner, statusOf, Switch, type YtAuthStatus,
} from '@/components/google-ads/youtube/youtubeShared';

export type ViewMode = 'list' | 'grid';
export type StatusFilter = 'all' | 'active' | 'paused' | 'removed';
export type SortKey = 'newest' | 'name' | 'budget';

/** List / grid, remembered per workspace. */
export function useViewMode(storageKey: string): [ViewMode, (v: ViewMode) => void] {
  const [view, setView] = useState<ViewMode>(() => {
    try { return (localStorage.getItem(storageKey) as ViewMode) || 'list'; } catch { return 'list'; }
  });
  const set = useCallback((v: ViewMode) => {
    setView(v);
    try { localStorage.setItem(storageKey, v); } catch { /* storage unavailable */ }
  }, [storageKey]);
  return [view, set];
}

export const Skeleton = ({ count = 3, indent = '', h = 'h-10' }: { count?: number; indent?: string; h?: string }) => (
  <div className={clsx('space-y-2 py-2', indent)}>{Array.from({ length: count }, (_, i) => <div key={i} className={clsx('skeleton rounded-xl', h)} />)}</div>
);

/** Search / status / sort over one objective's campaigns (removed ones only under their own chip). */
export function useCampaignFilters(campaigns: GoogleAdsCampaignSummary[] | null) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('newest');
  const [status, setStatus] = useState<StatusFilter>('all');
  const live = useMemo(() => (campaigns ?? []).filter((c) => !!c.id && c.status !== 'REMOVED'), [campaigns]);
  const removed = useMemo(() => (campaigns ?? []).filter((c) => !!c.id && c.status === 'REMOVED'), [campaigns]);
  const visible = useMemo(() => (status === 'removed' ? removed : live)
    .filter((c) => status === 'all' || status === 'removed' || c.status === (status === 'active' ? 'ENABLED' : 'PAUSED'))
    .filter((c) => `${c.name} ${c.id}`.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => (sort === 'name' ? a.name.localeCompare(b.name) : sort === 'budget' ? Number(b.budgetAmountMicros || 0) - Number(a.budgetAmountMicros || 0) : 0)),
  [live, removed, status, query, sort]);
  const filtering = !!query || status !== 'all';
  const clear = () => { setQuery(''); setStatus('all'); };
  return { query, setQuery, sort, setSort, status, setStatus, live, removed, visible, filtering, clear };
}

/** Enable (with a spend warning) / pause a campaign, then reload. */
export function useCampaignToggle(accountUid: string, reload: () => Promise<void> | void) {
  const [busy, setBusy] = useState<string | null>(null);
  const toggle = async (c: GoogleAdsCampaignSummary) => {
    const enable = c.status !== 'ENABLED';
    if (enable && !window.confirm(`Enable "${c.name}"? It will start spending its budget.`)) return;
    setBusy(c.id);
    try {
      if (enable) await googleAdsService.resumeCampaign(accountUid, c.id); else await googleAdsService.pauseCampaign(accountUid, c.id);
      toast.success(enable ? 'Campaign enabled' : 'Campaign paused');
      await reload();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };
  return { busy, toggle };
}

// ── toolbar · stats · filters ────────────────────────────────────────────

export function WorkspaceToolbar({ auth, onCustomer, onRefresh, refreshing, view, onView, onNew, extra }: {
  auth: YtAuthStatus | null | undefined; onCustomer: () => void; onRefresh: () => void; refreshing: boolean;
  view: ViewMode; onView: (v: ViewMode) => void; onNew: () => void; extra?: React.ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <div className="gads-toolbar mb-3 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-surface p-3">
      <button type="button" onClick={onCustomer} title="Switch Google Ads account"
        className="flex min-w-0 items-center gap-2 rounded-full border border-border bg-surface-alt px-3 py-1.5 text-sm text-text-primary transition-colors hover:border-primary/50">
        <Globe size={14} className="shrink-0 text-primary" />
        <span className="max-w-[160px] truncate sm:max-w-[240px]">{auth?.customer_name || auth?.customer_id}</span>
      </button>
      {extra}
      <span className="flex-1" />
      <button type="button" onClick={onRefresh} disabled={refreshing} title="Refresh" aria-label="Refresh"
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-50">
        {refreshing ? <Spinner size={14} /> : <RefreshCw size={15} />}
      </button>
      <Button variant="ghost" size="sm" onClick={() => navigate('/google-ads/tools')} iconLeft={<BarChart3 size={15} />}>Reports</Button>
      <div className="flex h-9 overflow-hidden rounded-lg border border-border">
        <button type="button" onClick={() => onView('list')} title="List view" aria-label="List view" aria-pressed={view === 'list'}
          className={clsx('px-2.5', view === 'list' ? 'bg-surface-alt text-text-primary' : 'text-text-muted hover:bg-surface-alt/50')}><List size={15} /></button>
        <button type="button" onClick={() => onView('grid')} title="Grid view" aria-label="Grid view" aria-pressed={view === 'grid'}
          className={clsx('px-2.5', view === 'grid' ? 'bg-surface-alt text-text-primary' : 'text-text-muted hover:bg-surface-alt/50')}><LayoutGrid size={15} /></button>
      </div>
      <Button onClick={onNew} iconLeft={<Plus size={15} />} className="h-9">
        <span className="hidden md:inline">New campaign</span><span className="md:hidden">New</span>
      </Button>
    </div>
  );
}

export interface StatTile { label: string; value: React.ReactNode; Icon: LucideIcon }

export const StatTiles = ({ stats, loading }: { stats: StatTile[]; loading: boolean }) => (
  <div className="gads-stats">
    {stats.map(({ label, value, Icon }) => (
      <div className="gads-stat" key={label}>
        <span className="gads-stat-label">{label}<Icon size={17} /></span>
        <span className="gads-stat-value">{loading ? '—' : value}</span>
      </div>
    ))}
  </div>
);

export function CampaignFilters({ f }: { f: ReturnType<typeof useCampaignFilters> }) {
  return (
    <>
      <div className="gads-filterbar mb-3 flex flex-wrap items-center gap-3">
        <label className="relative min-w-[200px] flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input aria-label="Search campaigns" placeholder="Search campaigns by name or ID…" className="pl-9" value={f.query} onChange={(e) => f.setQuery(e.target.value)} />
        </label>
        <label className="flex items-center gap-2">
          <ArrowUpDown size={14} className="text-text-muted" />
          <Select aria-label="Sort campaigns" value={f.sort} onChange={(e) => f.setSort(e.target.value as SortKey)} className="!w-auto">
            <option value="newest">Newest first</option><option value="name">Name A–Z</option><option value="budget">Daily budget</option>
          </Select>
        </label>
      </div>
      <div className="mb-3 flex items-center justify-between">
        <div className="gads-status-chips">
          <button type="button" aria-pressed={f.status === 'all'} onClick={() => f.setStatus('all')}>All campaigns</button>
          <button type="button" aria-pressed={f.status === 'active'} onClick={() => f.setStatus('active')}>Enabled</button>
          <button type="button" aria-pressed={f.status === 'paused'} onClick={() => f.setStatus('paused')}>Paused</button>
          {f.removed.length > 0 && <button type="button" aria-pressed={f.status === 'removed'} onClick={() => f.setStatus('removed')}>Removed ({f.removed.length})</button>}
        </div>
        {f.filtering && <button type="button" className="text-xs text-info" onClick={f.clear}>Clear filters</button>}
      </div>
    </>
  );
}

// ── gates ────────────────────────────────────────────────────────────────

export function ConnectGate({ auth, accountUid, onChanged }: { auth: YtAuthStatus; accountUid: string; onChanged: () => void }) {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!auth.connected) {
    return (
      <EmptyState icon={<Globe size={40} className="text-text-muted" />} title="No Google account connected" description="Connect Google in the Account page first."
        action={<Button onClick={() => navigate('/google-ads/account')} iconLeft={<Globe size={14} />}>Connect Google Ads</Button>} />
    );
  }
  return (
    <>
      <EmptyState icon={<Globe size={40} className="text-text-muted" />} title="Customer not attached yet" description="Pick which Google Ads customer this connection manages."
        action={<Button onClick={() => setPickerOpen(true)} iconLeft={<Plus size={14} />}>Choose account</Button>} />
      <AttachCustomerDialog open={pickerOpen} accountUid={accountUid} onClose={() => setPickerOpen(false)} onAttached={() => { setPickerOpen(false); onChanged(); }} />
    </>
  );
}

export function EmptyWorkspace({ icon: Icon, title, hint, onNew, points }: { icon: LucideIcon; title: string; hint: string; onNew: () => void; points?: string[] }) {
  return (
    <Card className="p-12 text-center">
      <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary"><Icon size={22} /></span>
      <p className="text-sm font-medium text-text-primary">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-text-muted">{hint}</p>
      {points && points.length > 0 && (
        <div className="mx-auto mt-4 flex max-w-xl flex-wrap justify-center gap-1.5">
          {points.map((p) => <span key={p} className="rounded-full border border-border bg-surface-alt px-2.5 py-1 text-2xs text-text-secondary">{p}</span>)}
        </div>
      )}
      <Button className="mt-5" onClick={onNew} iconLeft={<Plus size={15} />}>New campaign</Button>
    </Card>
  );
}

// ── rows ─────────────────────────────────────────────────────────────────

/** One campaign: header row (tile · name · tags · subline · status · switch) and, when open, its children. */
export function CampaignCard({ c, open, onToggle, tile, tags, subline, busy, onStatus, children }: {
  c: GoogleAdsCampaignSummary; open: boolean; onToggle: () => void; tile: React.ReactNode; tags?: React.ReactNode;
  subline: React.ReactNode; busy: boolean; onStatus: () => void; children?: React.ReactNode;
}) {
  const st = statusOf(c.status, c.primaryStatus);
  return (
    <Card padded={false} className="gads-campaign-card overflow-hidden rounded-2xl">
      <div className="gads-campaign-row">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-expanded={open} onClick={onToggle}>
          {open ? <ChevronDown size={18} className="shrink-0 text-text-muted" /> : <ChevronRight size={18} className="shrink-0 text-text-muted" />}
          {tile}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-[13px] font-semibold leading-tight text-text-primary">{c.name}</span>
              {tags}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-muted">
              {subline}
              {open && <CampaignDates c={c} />}
            </div>
          </div>
        </button>
        {c.primaryStatusReasons && c.primaryStatusReasons.length > 0 && c.status === 'ENABLED' && (
          <span className="hidden max-w-[220px] truncate text-[10px] text-text-muted xl:inline" title={c.primaryStatusReasons.join(', ')}>
            {c.primaryStatusReasons.map((r) => r.replace(/_/g, ' ').toLowerCase()).join(', ')}
          </span>
        )}
        <Pill {...st} />
        <Switch checked={c.status === 'ENABLED'} disabled={busy || c.status === 'REMOVED'} label={`Enable ${c.name}`} onCheckedChange={onStatus} />
      </div>
      {open && <div className="gads-campaign-detail">{children}</div>}
    </Card>
  );
}

/** Channel tile next to a campaign name. */
export const ChannelTile = ({ icon: Icon, tone = 'primary' }: { icon: LucideIcon; tone?: 'primary' | 'success' | 'info' }) => (
  <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
    tone === 'success' ? 'bg-success-soft text-success' : tone === 'info' ? 'bg-info-soft text-info' : 'bg-primary-soft text-primary')}>
    <Icon size={16} />
  </span>
);

/** Rounded child row (ad group / asset group) with its own expandable body. */
export function ChildRow({ open, onToggle, lead, title, sub, actions, status, children }: {
  open: boolean; onToggle: () => void; lead: React.ReactNode; title: string; sub: React.ReactNode; actions?: React.ReactNode;
  status: React.ReactNode; children?: React.ReactNode;
}) {
  return (
    <div className={clsx('rounded-xl border transition-colors', open ? 'border-border-strong bg-surface' : 'border-border/70 bg-surface hover:border-border-strong')}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={onToggle} aria-expanded={open}>
          {open ? <ChevronDown size={16} className="shrink-0 text-text-muted" /> : <ChevronRight size={16} className="shrink-0 text-text-muted" />}
          {lead}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-text-primary">{title}</div>
            <div className="truncate text-xs text-text-muted">{sub}</div>
          </div>
        </button>
        {actions}
        {status}
      </div>
      {open && <div className="border-t border-border/50 px-3.5 pb-2">{children}</div>}
    </div>
  );
}

/** "Label  chip chip chip +3" line in an expanded child row. */
export function ChipLine({ label, items, empty, max = 12, tone = 'plain' }: { label: string; items: string[]; empty?: string; max?: number; tone?: 'plain' | 'muted' }) {
  if (!items.length && !empty) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">{label}</span>
      {items.length === 0 && <span className="text-2xs text-text-muted">{empty}</span>}
      {items.slice(0, max).map((x) => (
        <span key={x} className={clsx('rounded-full px-2 py-0.5 text-2xs', tone === 'muted' ? 'bg-surface-alt text-text-muted' : 'border border-border bg-surface text-text-secondary')}>{x}</span>
      ))}
      {items.length > max && <span className="text-2xs text-text-muted">+{items.length - max} more</span>}
    </div>
  );
}
